import type { LineSeriesOption } from "echarts/charts";
import type { GridComponentOption, TitleComponentOption, TooltipComponentOption } from "echarts/components";
import type { ComposeOption } from "echarts/core";
import { getUnitCategoryConfiguration } from "verdi-common/Configurations/Units/UnitCategory/UnitCategoryConfigurations";
import { UnitConfigurations } from "verdi-common/Configurations/Units/UnitConfigurations";
import { AxisSide, type AxisSpec, type TimeAxisPanelSpec } from "../ChartSpec";
import { formatTooltipValue } from "../lib/format-tooltip-value";
import type { GetDisplayInfo } from "../units/use-get-display-info";
import type { PreparedSeries } from "./prepare-series";

type ECOption = ComposeOption<LineSeriesOption | TitleComponentOption | TooltipComponentOption | GridComponentOption>;

/** Horizontal spacing between stacked y-axes on the same side. */
const Y_AXIS_STACK_OFFSET_PX = 40;

/** Approximate px width per character at the axis name font size (12px). */
const AXIS_NAME_CHAR_WIDTH_PX = 7.5;
/** Total horizontal padding inside the axis name badge (4px each side). */
const AXIS_NAME_BADGE_PADDING_PX = 8;

function longestSymbolChars(unitCategory: string): number {
    const config = getUnitCategoryConfiguration(unitCategory);
    if (config) {
        return Math.max(...config.allowedUnits.map((u) => UnitConfigurations[u].symbol.length));
    }
    return unitCategory.length;
}

/** Pre-compute a grid margin from the worst-case badge width for each axis on one side.
 *  ECharts auto-expands grid.left / grid.right to fit axis names — by setting the maximum
 *  possible value upfront, switching units never causes a layout shift. */
export function computeGridMargin(axes: AxisSpec[], side: AxisSpec["side"]): number {
    let maxRequired = 0;
    let indexOnSide = 0;
    axes.forEach((axis) => {
        if (axis.side !== side) return;
        const axisOffset = 10 + indexOnSide * Y_AXIS_STACK_OFFSET_PX;
        const charLen = axis.label?.length ?? longestSymbolChars(axis.unitCategory);
        const badgeWidth = Math.ceil(charLen * AXIS_NAME_CHAR_WIDTH_PX) + AXIS_NAME_BADGE_PADDING_PX;
        maxRequired = Math.max(maxRequired, axisOffset + badgeWidth + 4);
        indexOnSide++;
    });
    return maxRequired;
}

export function computeGridMargins(axes: AxisSpec[]): { left: number; right: number } {
    return {
        left: computeGridMargin(axes, AxisSide.Left),
        right: computeGridMargin(axes, AxisSide.Right),
    };
}

const GRID_BORDER_COLOR = "#E4E5E2";
const AXIS_NAME_TEXT_COLOR = "#31332E";

/** Pull axisPointer label up into the band between grid bottom and tick labels. */
const X_AXIS_POINTER_LABEL_MARGIN = 0;
/** Push tick labels down so they sit below the crosshair badge. */
const X_AXIS_TICK_LABEL_MARGIN = 14;

const buildYAxes = (axes: AxisSpec[], getDisplayInfo: GetDisplayInfo): ECOption["yAxis"] => {
    const indexBySide: Record<AxisSpec["side"], number> = { left: 0, right: 0 };

    return axes.map((axis) => {
        const indexOnSide = indexBySide[axis.side]++;

        // For an unknown category getDisplayInfo has no symbol; show the raw category name as an
        // "unsupported unit" hint so the axis is still identifiable (values render raw/unconverted).
        const displayInfo = getDisplayInfo(axis.unitCategory);
        const axisName = axis.label ?? (displayInfo.isKnown ? displayInfo.symbol : axis.unitCategory);

        return {
            id: axis.id,
            triggerEvent: true,
            name: axisName,
            nameLocation: "end",
            nameGap: 0,
            nameRotate: 0,
            splitNumber: 4,
            nameMoveOverlap: true,
            nameTextStyle: {
                align: "right",
                verticalAlign: "top",
                color: AXIS_NAME_TEXT_COLOR,
                fontSize: 12,
                backgroundColor: "#FFFFFF",
                borderColor: GRID_BORDER_COLOR,
                borderWidth: 1,
                borderRadius: 8,
                padding: [4, 4, 4, 4],
            },
            type: "value",
            position: axis.side,
            offset: 10 + indexOnSide * Y_AXIS_STACK_OFFSET_PX,
            axisLabel: {
                color: "#838879",
                fontWeight: "lighter",
                fontSize: 12,
                showMaxLabel: false,
                interval: 0,
                hideOverlap: true,
                margin: 0,
            },
            splitLine: {
                show: false,
            },
            axisPointer: {
                label: {
                    formatter: (params) =>
                        typeof params.value === "number" ? formatTooltipValue(params.value) : String(params.value),
                },
            },
        };
    });
};

export const buildTimePanelOptions = (
    spec: TimeAxisPanelSpec,
    series: PreparedSeries[],
    getDisplayInfo: GetDisplayInfo,
    gridMargins: { left: number; right: number },
    window: { startMs: number; endMs: number },
): ECOption => ({
    // Draw a border around the entire chart
    grid: {
        show: true,
        borderColor: GRID_BORDER_COLOR,
        borderWidth: 1,
        backgroundColor: "transparent",
        left: gridMargins.left,
        right: gridMargins.right,
        top: 0,
        bottom: 0,
    },
    tooltip: {
        trigger: "axis",
        formatter: (params) => {
            if (!Array.isArray(params)) return "";
            return params
                .map((p) => {
                    const value = Array.isArray(p.value) ? p.value[1] : p.value;
                    const formatted = value != null ? formatTooltipValue(value as number) : "—";
                    return `${p.marker} ${p.seriesName}: ${formatted}`;
                })
                .join("<br/>");
        },
        valueFormatter: (value) => formatTooltipValue(value as number),
        axisPointer: {
            type: "cross",
            snap: false,
            label: {
                color: "#FDFDFD",
                backgroundColor: "#31332E",
                padding: [0, 2],
                fontSize: 12,
                fontWeight: 400,
                borderRadius: 0,
                margin: -10,
                align: "left",
            },
        },
    },
    xAxis: {
        type: "time",
        // The x-axis should show the user's desired time range - not whatever the actual set of
        // downloaded data starts and ends at.
        min: window.startMs,
        max: window.endMs,
        tooltip: {
            show: false,
        },
        axisLine: {
            show: false,
        },
        axisPointer: {
            label: {
                margin: X_AXIS_POINTER_LABEL_MARGIN,
            },
        },
        axisLabel: {
            color: "#838879",
            margin: X_AXIS_TICK_LABEL_MARGIN,
            showMaxLabel: false,
            showMinLabel: false,
        },
        axisTick: {
            show: false,
        },
        splitLine: {
            show: true,
            lineStyle: {
                color: GRID_BORDER_COLOR,
            },
        },
    },
    yAxis: buildYAxes(spec.axes, getDisplayInfo),
    series: series.map((s) => {
        const entry = spec.streams.find((stream) => stream.streamId === s.streamId);
        const axis = spec.axes.find((a) => a.id === entry?.axisId);

        return {
            id: String(s.streamId),
            yAxisId: axis?.id,
            name: s.name,
            data: s.points,
            type: "line",
            showSymbol: false,
            color: s.color,
        };
    }),
});
